import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";
import { toast } from "sonner";
import { ArrowLeft, Briefcase, CheckCircle, Clock, Star, FileText, Users } from "lucide-react";
import { AnimatedPage } from "../BizDevAnimations";

const PROTECTED_IDS = ["user_2b35c4ad89d1", "user_c72102e8ac64"];

export default function TeamMemberPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [member, setMember] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [reviewTasks, setReviewTasks] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  useEffect(() => { loadMemberData(); }, [id]);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const loadMemberData = async () => {
    setLoading(true);
    try {
      const userRes = await api.post("/users/resolve", { ids: [id] });
      const userData = userRes.data?.[id] || { name: id, designation: "" };
      setMember(userData);
      // Fetch tasks assigned to this member + all team tasks (to find reviewer tasks)
      const [tasksAssignedRes, teamRes, briefsRes] = await Promise.all([
        api.get(`/tasks?assignee_id=${id}`),
        api.get("/tasks/team").catch(() => ({ data: [] })),
        api.get("/bizdev/briefs").catch(() => ({ data: [] })),
      ]);
      const assignedTasks = tasksAssignedRes.data || [];
      const allTeamTasks = teamRes.data || [];
      // Tasks where this member is the reviewer (but not the assignee)
      const revTasks = allTeamTasks.filter(t => t.reviewer_id === id && t.assignee_id !== id);
      setTasks(assignedTasks);
      setReviewTasks(revTasks);
      const allBriefs = briefsRes.data || [];
      const memberBriefs = allBriefs.filter(b =>
        b.assigned_to === id || b.sub_assigned_to === id || b.reviewer_1 === id || b.reviewer_2 === id
      );
      setBriefs(memberBriefs);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const recent = [];
      (assignedTasks).forEach(t => {
        if (t.updated_at && new Date(t.updated_at) > thirtyDaysAgo) {
          recent.push({ type: "task", action: t.status === "Done" ? "completed" : "updated", item: t, date: t.updated_at });
        }
      });
      memberBriefs.forEach(b => {
        if (b.updated_at && new Date(b.updated_at) > thirtyDaysAgo) {
          recent.push({ type: "brief", action: b.status === "Won" ? "won" : b.status === "Executed" ? "executed" : "updated", item: b, date: b.updated_at });
        }
      });
      recent.sort((a, b) => new Date(b.date) - new Date(a.date));
      setRecentActivity(recent);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load member data");
    } finally { setLoading(false); }
  };

  const formatDate = (d) => {
    if (!d) return "";
    try { return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return d; }
  };

  const completedTasks = tasks.filter(t => t.status === "Done");
  const activeTasks = tasks.filter(t => t.status !== "Done");
  const briefsToReview = briefs.filter(b => (b.reviewer_1 === id || b.reviewer_2 === id) && b.status !== "Won" && b.status !== "Lost");
  const briefsAssigned = briefs.filter(b => b.assigned_to === id || b.sub_assigned_to === id);
  const briefsWon = briefs.filter(b => (b.assigned_to === id || b.sub_assigned_to === id) && b.status === "Won");
  const briefsPitched = briefs.filter(b => (b.assigned_to === id || b.sub_assigned_to === id) && b.status === "Pitched");

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-[var(--t3)] text-xl font-heading">LOADING...</div></div>;

  const Stat = ({ icon: Icon, label, value, color }) => (
    <div className="glass-card p-4 text-center">
      <Icon size={20} className="mx-auto mb-1" style={{ color: color || "var(--c)" }} />
      <p className="text-2xl font-bold text-[var(--t1)]">{value}</p>
      <p className="text-xs text-[var(--t3)]">{label}</p>
    </div>
  );

  const activeReviewTasks = reviewTasks.filter(t => t.status !== "Done" && t.status !== "Completed");

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "tasks", label: `Tasks (${tasks.length})` },
    { key: "reviews", label: `Reviews (${activeReviewTasks.length + briefsToReview.length})` },
    { key: "briefs", label: `Briefs (${briefs.length})` },
    { key: "activity", label: "Recent Activity" },
  ];


  // Access restriction for protected profiles
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isProtectedProfile = PROTECTED_IDS.includes(id);
  const isCurrentUserProtected = PROTECTED_IDS.includes(currentUser?.id);
  if (isProtectedProfile && !isCurrentUserProtected) {
    return (
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"60vh",flexDirection:"column"}}>
        <div style={{fontSize:"3rem",marginBottom:"1rem"}}>{String.fromCodePoint(0x1F512)}</div>
        <h2 style={{color:"var(--t1)",marginBottom:"0.5rem"}}>Access Restricted</h2>
        <p style={{color:"var(--t3)"}}>You don&apos;t have permission to view this profile.</p>
      </div>
    );
  }

  return (
    <AnimatedPage>
      <div className="dm-page-container max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)} className="text-[var(--t3)] hover:text-[var(--t1)] transition-colors"><ArrowLeft size={20} /></button>
          <div>
            <h1 className="text-2xl font-heading text-[var(--t1)]">{member?.name || id}</h1>
            {member?.designation && <p className="text-sm text-[var(--t3)]">{member.designation}</p>}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-6">
          <Stat icon={CheckCircle} label="Tasks Done" value={completedTasks.length} color="#4ade80" />
          <Stat icon={Clock} label="Active Tasks" value={activeTasks.length} color="#F59E0B" />
          <Stat icon={FileText} label="Briefs Assigned" value={briefsAssigned.length} color="#8B5CF6" />
          <Stat icon={Users} label="To Review" value={briefsToReview.length} color="#06B6D4" />
          <Stat icon={Star} label="Briefs Won" value={briefsWon.length} color="#4ade80" />
          <Stat icon={Briefcase} label="Pitched" value={briefsPitched.length} color="#A855F7" />
        </div>
        <div className="flex gap-1 mb-4 overflow-x-auto">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.key ? "bg-[var(--s3)] text-[var(--t1)]" : "text-[var(--t3)] hover:text-[var(--t1)]"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="space-y-4">
            <div className="glass-card p-4">
              <h3 className="text-sm font-medium text-[var(--t3)] mb-3">Active Tasks</h3>
              {activeTasks.length === 0 ? <p className="text-[var(--t4)] text-sm">No active tasks</p> : (
                <div className="space-y-2">
                  {activeTasks.slice(0, 5).map(t => (
                    <div key={t.task_id} onClick={() => navigate(`/tasks/${t.task_id}`)}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-[var(--s2)] cursor-pointer transition-colors">
                      <div>
                        <p className="text-[var(--t1)] text-sm">{t.title}</p>
                        <p className="text-xs text-[var(--t3)]">{t.priority} &bull; {t.status}</p>
                      </div>
                      {t.due_date && <span className="text-xs text-[var(--t4)]">{formatDate(t.due_date)}</span>}
                    </div>
                  ))}
                  {activeTasks.length > 5 && <p className="text-xs text-[var(--t4)] text-center">+{activeTasks.length - 5} more</p>}
                </div>
              )}
            </div>
            {activeReviewTasks.length > 0 && (
            <div className="glass-card p-4">
              <h3 className="text-sm font-medium text-[var(--t3)] mb-3">Tasks to Review ({activeReviewTasks.length})</h3>
              <div className="space-y-2">
                {activeReviewTasks.slice(0, 5).map(t => (
                  <div key={t.task_id} onClick={() => navigate(`/tasks/${t.task_id}`)}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-[var(--s2)] cursor-pointer transition-colors">
                    <div>
                      <p className="text-[var(--t1)] text-sm">{t.title}</p>
                      <p className="text-xs text-[var(--t3)]">{t.assignee_name || ''} &bull; {t.status}</p>
                    </div>
                    {t.due_date && <span className="text-xs text-[var(--t3)]">{formatDate(t.due_date)}</span>}
                  </div>
                ))}
                {activeReviewTasks.length > 5 && <p className="text-xs text-[var(--t3)] text-center">+{activeReviewTasks.length - 5} more</p>}
              </div>
            </div>
            )}
            <div className="glass-card p-4">
              <h3 className="text-sm font-medium text-[var(--t3)] mb-3">Briefs to Review</h3>
              {briefsToReview.length === 0 ? <p className="text-[var(--t4)] text-sm">No briefs to review</p> : (
                <div className="space-y-2">
                  {briefsToReview.slice(0, 5).map(b => (
                    <div key={b._id || b.brief_id} onClick={() => navigate(`/bizdev/briefs/${b.brief_id || b._id}`)}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-[var(--s2)] cursor-pointer transition-colors">
                      <div>
                        <p className="text-[var(--t1)] text-sm">{b.title || b.client_name}</p>
                        <p className="text-xs text-[var(--t3)]">{b.status}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {briefsWon.length > 0 && (
              <div className="glass-card p-4">
                <h3 className="text-sm font-medium text-[var(--t3)] mb-3">Won Briefs</h3>
                <div className="space-y-2">
                  {briefsWon.map(b => (
                    <div key={b._id || b.brief_id} onClick={() => navigate(`/bizdev/briefs/${b.brief_id || b._id}`)}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-[var(--s2)] cursor-pointer transition-colors">
                      <p className="text-[var(--t1)] text-sm">{b.title || b.client_name}</p>
                      <p className="text-xs text-[#4ade80]">Won</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "tasks" && (
          <div className="space-y-2">
            {tasks.length === 0 ? <p className="text-[var(--t4)] text-sm text-center py-8">No tasks</p> :
              tasks.map(t => (
                <div key={t.task_id} onClick={() => navigate(`/tasks/${t.task_id}`)}
                  className="glass-card p-3 flex items-center gap-3 cursor-pointer hover:border-[var(--bdrh)] transition-colors">
                  <div className={`w-3 h-3 rounded-full ${t.status === "Done" ? "bg-green-400" : t.status === "In Progress" ? "bg-yellow-400" : "bg-[#555]"}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${t.status === "Done" ? "text-[var(--t4)] line-through" : "text-[var(--t1)]"}`}>{t.title}</p>
                    <div className="flex items-center gap-2 text-xs text-[var(--t3)]">
                      <span>{t.priority}</span>
                      <span>&bull;</span>
                      <span>{t.status}</span>
                      {t.due_date && <><span>&bull;</span><span>{formatDate(t.due_date)}</span></>}
                    </div>
                  </div>
                </div>
              ))
            }
          </div>
        )}

        {tab === "briefs" && (
          <div className="space-y-2">
            {briefs.length === 0 ? <p className="text-[var(--t4)] text-sm text-center py-8">No briefs</p> :
              briefs.map(b => {
                const role = b.assigned_to === id ? "Owner" : b.sub_assigned_to === id ? "Support" : b.reviewer_1 === id ? "Reviewer 1" : "Reviewer 2";
                return (
                  <div key={b._id || b.brief_id} onClick={() => navigate(`/bizdev/briefs/${b.brief_id || b._id}`)}
                    className="glass-card p-3 flex items-center gap-3 cursor-pointer hover:border-[var(--bdrh)] transition-colors">
                    <div className="flex-1 min-w-0">
                      <p className="text-[var(--t1)] text-sm">{b.title || b.client_name}</p>
                      <div className="flex items-center gap-2 text-xs text-[var(--t3)]">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${b.status === "Won" ? "bg-green-400/20 text-green-400" : b.status === "Lost" ? "bg-red-400/20 text-red-400" : "bg-[var(--s2)] text-[var(--t3)]"}`}>{b.status}</span>
                        <span>{role}</span>
                      </div>
                    </div>
                  </div>
                );
              })
            }
          </div>
        )}

        {tab === "reviews" && (
          <div className="space-y-4">
            {/* Tasks to review */}
            <div className="glass-card p-4">
              <h3 className="text-sm font-medium text-[var(--t3)] mb-3">Tasks to Review ({activeReviewTasks.length})</h3>
              {activeReviewTasks.length === 0 ? <p className="text-[var(--t3)] text-sm">No tasks to review</p> : (
                <div className="space-y-2">
                  {activeReviewTasks.map(t => (
                    <div key={t.task_id} onClick={() => navigate(`/tasks/${t.task_id}`)}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-[var(--s2)] cursor-pointer transition-colors">
                      <div>
                        <p className="text-[var(--t1)] text-sm">{t.title}</p>
                        <p className="text-xs text-[var(--t3)]">{t.assignee_name || 'Unassigned'} &bull; {t.priority} &bull; {t.status}</p>
                      </div>
                      {t.due_date && <span className="text-xs text-[var(--t3)]">{formatDate(t.due_date)}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Briefs to review */}
            <div className="glass-card p-4">
              <h3 className="text-sm font-medium text-[var(--t3)] mb-3">Briefs to Review ({briefsToReview.length})</h3>
              {briefsToReview.length === 0 ? <p className="text-[var(--t3)] text-sm">No briefs to review</p> : (
                <div className="space-y-2">
                  {briefsToReview.map(b => (
                    <div key={b._id || b.brief_id} onClick={() => navigate(`/bizdev/briefs/${b.brief_id || b._id}`)}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-[var(--s2)] cursor-pointer transition-colors">
                      <div>
                        <p className="text-[var(--t1)] text-sm">{b.title || b.client_name}</p>
                        <p className="text-xs text-[var(--t3)]">{b.company_name || ''} &bull; {b.status}</p>
                      </div>
                      {b.pitch_date && <span className="text-xs" style={{ color: '#E31937' }}>{formatDate(b.pitch_date)}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "activity" && (
          <div className="space-y-2">
            <p className="text-xs text-[var(--t3)] mb-2">Last 30 days</p>
            {recentActivity.length === 0 ? <p className="text-[var(--t4)] text-sm text-center py-8">No recent activity</p> :
              recentActivity.map((a, i) => (
                <div key={i} onClick={() => navigate(a.type === "task" ? `/tasks/${a.item.task_id}` : `/bizdev/briefs/${a.item.brief_id || a.item._id}`)}
                  className="glass-card p-3 flex items-center gap-3 cursor-pointer hover:border-[var(--bdrh)] transition-colors">
                  <div className={`w-2 h-2 rounded-full ${a.action === "completed" || a.action === "won" ? "bg-green-400" : a.action === "executed" ? "bg-blue-400" : "bg-yellow-400"}`} />
                  <div className="flex-1">
                    <p className="text-[var(--t1)] text-sm">
                      {a.action === "completed" ? "Completed task" : a.action === "won" ? "Won brief" : a.action === "executed" ? "Executed brief" : "Updated " + a.type}: {a.item.title || a.item.client_name}
                    </p>
                    <p className="text-xs text-[var(--t3)]">{formatDate(a.date)}</p>
                  </div>
                </div>
              ))
            }
          </div>
        )}
      </div>
    </AnimatedPage>
  );
}
